import React, { useState } from 'react';
import { Button, FormControl, FormLabel, Input, Sheet, Snackbar, Stack, Avatar, Select, Option, Typography, FormHelperText, Divider } from '@mui/joy';
import { Report } from '@mui/icons-material';
import { faGooglePlay, faAppStore } from '@fortawesome/free-brands-svg-icons'

import LoadingSpinnerComponent from '../LoadingSpinnerComponent';

import '../../styles/navigation.scss';
import '../../styles/stage.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { searchIOSApp, searchAndroidApp } from '../../Networking/Apps/AppsAPI';
import { useAuth } from '../../context/authContext';

const networking = require('../../Networking/API');

function AddNewAppComponent({ subscription, onAppAdded }: any) {
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [tempAppName, setTempAppName] = useState(null);
    const [iconURL, setIconURL] = useState('');
    const [countries, setCountries] = useState(null) as any[];
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [searchResults, setSearchResults] = useState([]) as any[];
    const [isSearching, setIsSearching] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null) as any;
    const [androidSearchQuery, setAndroidSearchQuery] = useState('');
    const [androidSearchResults, setAndroidSearchResults] = useState([]) as any[];
    const [isAndroidSearching, setIsAndroidSearching] = useState(false);
    const [selectedAndroidApp, setSelectedAndroidApp] = useState(null) as any;

    const huaweiIcon = <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 48 48">
        <path fill="#e53935" d="M15,6h18c4.971,0,9,4.029,9,9v18c0,4.971-4.029,9-9,9H15c-4.971,0-9-4.029-9-9V15 C6,10.029,10.029,6,15,6z"></path><path fill="#fff" d="M21.775,20.935c-1.506,0-2.669,1.312-2.669,3.716c0,1.571,2.378,5.599,4.486,9.127 C23.592,24.644,23.368,23.655,21.775,20.935z"></path><path fill="#fff" d="M26.225,20.935c-1.624,2.674-1.817,3.711-1.817,12.846c2.108-3.528,4.486-7.558,4.486-9.13 C28.894,22.245,27.731,20.935,26.225,20.935z"></path><path fill="#fff" d="M16.581,23.904c-2.21,1.543-1.484,5.192-0.369,6.386c0.735,0.771,3.596,2.159,6.305,4 L16.581,23.904z"></path><path fill="#fff" d="M31.419,23.904l-5.936,10.385c2.709-1.84,5.57-3.229,6.305-4 C32.903,29.096,33.629,25.447,31.419,23.904z"></path><path fill="#fff" d="M13.612,29.84c0.036,4.111,2.545,5.191,4.241,5.191h4.241L13.612,29.84z"></path><path fill="#fff" d="M34.388,29.84l-8.483,5.191h4.241C31.843,35.031,34.352,33.951,34.388,29.84z"></path><path fill="#fff" d="M15.926,35.775C16.163,37.002,16.698,38,18.239,38c1.542,0,3.035-0.889,3.855-2.225H15.926z"></path><path fill="#fff" d="M25.905,35.775C26.726,37.111,28.219,38,29.761,38s2.076-0.998,2.314-2.225H25.905z"></path><circle cx="14.5" cy="10.5" r="1.5" fill="#b71c1c"></circle><circle cx="33.5" cy="10.5" r="1.5" fill="#b71c1c"></circle><path fill="none" stroke="#fff" stroke-linecap="round" stroke-miterlimit="10" d="M33.5,10.5c0,0-2,5-9.5,5s-9.5-5-9.5-5"></path>
    </svg>


    if (!countries) {
        networking.getCountries().then((data: any) => {
            setCountries(data);
            setLoading(false);
        }).catch((error: Error) => {
            setError(error.message);
            setLoading(false);
        });
    }

    const handleSearchQueryChange = async (event: any) => {
        const value = event.target.value;
        setSearchQuery(value);

        if (value.length >= 3 && selectedCountry) {
            setIsSearching(true);
            try {
                const query = `name=${value}&country=${selectedCountry}`;
                const results = await searchIOSApp(query, token ?? "");
                setSearchResults(results.results || []);
            } catch (error: any) {
                console.error('Error searching iOS apps:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }
    }

    const handleCountryChange = (event: any, newValue: string | null) => {
        setSelectedCountry(newValue || '');
        // Clear search results when country changes
        setSearchResults([]);
        setSearchQuery('');
        setSelectedApp(null);
        setAndroidSearchResults([]);
        setAndroidSearchQuery('');
        setSelectedAndroidApp(null);
    }

    const selectApp = (app: any) => {
        setSelectedApp(app);
        setSearchResults([]);
        setSearchQuery('');
        if (app.artworkUrl512) {
            setIconURL(app.artworkUrl512);
        }
    }

    const handleAndroidSearchQueryChange = async (event: any) => {
        const value = event.target.value;
        setAndroidSearchQuery(value);

        if (value.length >= 3 && selectedCountry) {
            setIsAndroidSearching(true);
            try {
                const query = `name=${value}&country=${selectedCountry}`;
                const results = await searchAndroidApp(query, token ?? "");
                setAndroidSearchResults(results.results || []);
            } catch (error: any) {
                console.error('Error searching Android apps:', error);
                setAndroidSearchResults([]);
            } finally {
                setIsAndroidSearching(false);
            }
        } else {
            setAndroidSearchResults([]);
        }
    }

    const selectAndroidApp = (app: any) => {
        setSelectedAndroidApp(app);
        setAndroidSearchResults([]);
        setAndroidSearchQuery('');
        if (app.icon) {
            setIconURL(app.icon);
        }
    }

    const saveApp = (event: any) => {
        setLoading(true);

        const formData = new FormData(event.currentTarget);
        const formJson: any = Object.fromEntries(formData.entries());
        let country = countries.find((country: any) => country.code === formJson.countryCode)
        formJson.country = country.name;

        // Add selected iOS app data if available
        if (selectedApp) {
            formJson.appleAppId = selectedApp.trackId;

        }

        // Add selected Android app data if available  
        if (selectedAndroidApp) {
            formJson.androidPackageId = selectedAndroidApp.appId;
        }

        if (selectedApp && selectedApp.artworkUrl100) {
            formJson.icon = selectedApp.artworkUrl100;
        }

        networking.saveApp(formJson, subscription).then((app: any) => {
            setLoading(false);
            setTempAppName(app.name);
            setShowSuccess(true);
            onAppAdded();
        }).catch((error: Error) => {
            window.scrollTo(0, 0);
            setShowError(true);
            setError(error.message);
            setLoading(false);
        });
    }

    return (
        <>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            {showSuccess &&
                <Snackbar
                    variant="soft"
                    color="success"
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowSuccess(false)}
                            size="sm"
                            variant="soft"
                            color="success"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    You new App {tempAppName} was added! 🚀
                </Snackbar>
            }

            {loading ?
                <LoadingSpinnerComponent />
                :
                <div className=''>
                    <form
                        onSubmit={(event) => {
                            saveApp(event)
                        }}
                    >
                        <Stack
                            spacing={4}>
                            <Sheet
                                className='bg-white p-4 rounded'>
                                <div className='row'>
                                    <div className='col'>
                                        <FormControl
                                            sx={{
                                                marginTop: 2
                                            }}>
                                            <FormLabel
                                                required
                                                sx={{ fontSize: '12px' }}>
                                                App name
                                            </FormLabel>
                                            <Input
                                                name="name"
                                                type="text"
                                                placeholder="Your app name"
                                                size='lg'
                                                required
                                                sx={{ fontSize: '14px' }}
                                            />
                                        </FormControl>
                                    </div>
                                    <div className='col'>
                                        <FormControl
                                            sx={{
                                                marginTop: 2
                                            }}>
                                            <FormLabel
                                                required
                                                sx={{ fontSize: '12px' }}>
                                                Primary Source Country
                                            </FormLabel>
                                            <Select
                                                name='countryCode'
                                                color="neutral"
                                                size="lg"
                                                variant="outlined"
                                                sx={{ fontSize: '14px' }}
                                                onChange={handleCountryChange}
                                                required
                                            >
                                                {
                                                    countries && countries.map((country: any) => (
                                                        <Option
                                                            key={country.code}
                                                            value={country.code}
                                                            sx={{ fontSize: '14px' }}>
                                                            {country.name}
                                                        </Option>
                                                    ))
                                                }
                                            </Select>
                                            <FormHelperText sx={{ fontSize: '12px', fontWeight: 'medium' }}>
                                                You can add more countries later.
                                            </FormHelperText>
                                        </FormControl>
                                    </div>
                                </div>
                            </Sheet>
                        </Stack>
                        <Stack
                            spacing={2}>
                            <Sheet
                                className='bg-white p-4 pt-0 rounded'>
                                <Typography
                                    sx={{
                                        marginBottom: 1
                                    }}
                                    level="title-sm">
                                    Source App Identifiers
                                </Typography>
                                <Divider className='mb-4' />

                                {/* App Store Search Field */}
                                <FormControl sx={{ marginBottom: 2 }}>
                                    <FormLabel sx={{ fontSize: '12px' }}>
                                        <span className='me-2'>
                                            <FontAwesomeIcon icon={faAppStore} color='#111111' />
                                        </span>
                                        Search App Store
                                    </FormLabel>
                                    <Input
                                        type="text"
                                        placeholder="Search for your iOS app..."
                                        size='lg'
                                        sx={{ fontSize: '14px' }}
                                        value={searchQuery}
                                        onChange={handleSearchQueryChange}
                                        disabled={!selectedCountry}
                                        endDecorator={isSearching ? <Typography level="body-sm">Searching...</Typography> : null}
                                    />
                                    <FormHelperText sx={{ fontSize: '12px', fontWeight: 'medium' }}>
                                        {!selectedCountry ? 'Please select a country first to enable app search.' : 'Type at least 3 characters to search for iOS apps in the selected country.'}
                                    </FormHelperText>

                                    {/* iOS Search Results */}
                                    {searchResults.length > 0 && (
                                        <Sheet
                                            variant="outlined"
                                            sx={{
                                                marginTop: 1,
                                                maxHeight: '300px',
                                                overflowY: 'auto',
                                                borderRadius: 'sm'
                                            }}
                                        >
                                            <Stack spacing={1} sx={{ p: 1 }}>
                                                {searchResults.map((app: any) => (
                                                    <Sheet
                                                        key={app.trackId}
                                                        sx={{
                                                            p: 1.5,
                                                            cursor: 'pointer',
                                                            borderRadius: 'sm',
                                                            '&:hover': {
                                                                bgcolor: 'background.level1'
                                                            }
                                                        }}
                                                        onClick={() => selectApp(app)}
                                                    >
                                                        <div className='d-flex align-items-center'>
                                                            {app.artworkUrl60 && (
                                                                <Avatar
                                                                    src={app.artworkUrl60}
                                                                    alt={app.trackName}
                                                                    sx={{ marginRight: 2, width: 40, height: 40 }}
                                                                />
                                                            )}
                                                            <div>
                                                                <Typography level="body-sm" fontWeight="md">
                                                                    {app.trackName}
                                                                </Typography>
                                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                                    {app.artistName} • ID: {app.trackId}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </Sheet>
                                                ))}
                                            </Stack>
                                        </Sheet>
                                    )}
                                </FormControl>

                                {/* Selected iOS App Display */}
                                {selectedApp && (
                                    <Sheet
                                        variant="soft"
                                        color="neutral"
                                        sx={{
                                            p: 2,
                                            borderRadius: 'sm',
                                            marginBottom: 2
                                        }}
                                    >
                                        <div className='d-flex align-items-center'>
                                            {selectedApp.artworkUrl60 && (
                                                <Avatar
                                                    src={selectedApp.artworkUrl60}
                                                    alt={selectedApp.trackName}
                                                    sx={{ marginRight: 2, width: 40, height: 40 }}
                                                />
                                            )}
                                            <div>
                                                <Typography level="body-sm" fontWeight="md">
                                                    {selectedApp.trackName}
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                    {selectedApp.artistName} • ID: {selectedApp.trackId}
                                                </Typography>
                                            </div>
                                        </div>
                                    </Sheet>
                                )}

                                {/* Google Play Search Field */}
                                <FormControl sx={{ marginBottom: 2, marginTop: 2 }}>
                                    <FormLabel sx={{ fontSize: '12px' }}>
                                        <span className='me-2'>
                                            <FontAwesomeIcon icon={faGooglePlay} color='#00C853' />
                                        </span>
                                        Search Google Play Store
                                    </FormLabel>
                                    <Input
                                        type="text"
                                        placeholder="Search for your Android app..."
                                        size='lg'
                                        sx={{ fontSize: '14px' }}
                                        value={androidSearchQuery}
                                        onChange={handleAndroidSearchQueryChange}
                                        disabled={!selectedCountry}
                                        endDecorator={isAndroidSearching ? <Typography level="body-sm">Searching...</Typography> : null}
                                    />
                                    <FormHelperText sx={{ fontSize: '12px', fontWeight: 'medium' }}>
                                        {!selectedCountry ? 'Please select a country first to enable app search.' : 'Type at least 3 characters to search for Android apps in the selected country.'}
                                    </FormHelperText>

                                    {/* Android Search Results */}
                                    {androidSearchResults.length > 0 && (
                                        <Sheet
                                            variant="outlined"
                                            sx={{
                                                marginTop: 1,
                                                maxHeight: '300px',
                                                overflowY: 'auto',
                                                borderRadius: 'sm'
                                            }}
                                        >
                                            <Stack spacing={1} sx={{ p: 1 }}>
                                                {androidSearchResults.map((app: any) => (
                                                    <Sheet
                                                        key={app.appId}
                                                        sx={{
                                                            p: 1.5,
                                                            cursor: 'pointer',
                                                            borderRadius: 'sm',
                                                            '&:hover': {
                                                                bgcolor: 'background.level1'
                                                            }
                                                        }}
                                                        onClick={() => selectAndroidApp(app)}
                                                    >
                                                        <div className='d-flex align-items-center'>
                                                            {app.icon && (
                                                                <Avatar
                                                                    src={app.icon}
                                                                    alt={app.title}
                                                                    sx={{ marginRight: 2, width: 40, height: 40 }}
                                                                />
                                                            )}
                                                            <div>
                                                                <Typography level="body-sm" fontWeight="md">
                                                                    {app.title}
                                                                </Typography>
                                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                                    {app.developer} • ID: {app.appId}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </Sheet>
                                                ))}
                                            </Stack>
                                        </Sheet>
                                    )}
                                </FormControl>

                                {/* Selected Android App Display */}
                                {selectedAndroidApp && (
                                    <Sheet
                                        variant="soft"
                                        color="success"
                                        sx={{
                                            p: 2,
                                            borderRadius: 'sm',
                                            marginBottom: 2
                                        }}
                                    >
                                        <div className='d-flex align-items-center'>
                                            {selectedAndroidApp.icon && (
                                                <Avatar
                                                    src={selectedAndroidApp.icon}
                                                    alt={selectedAndroidApp.title}
                                                    sx={{ marginRight: 2, width: 40, height: 40 }}
                                                />
                                            )}
                                            <div>
                                                <Typography level="body-sm" fontWeight="md">
                                                    {selectedAndroidApp.title}
                                                </Typography>
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                    {selectedAndroidApp.developer} • ID: {selectedAndroidApp.appId}
                                                </Typography>
                                            </div>
                                        </div>
                                    </Sheet>
                                )}

                                <FormControl
                                    sx={{
                                        marginTop: 2
                                    }}>
                                    <FormLabel
                                        sx={{ fontSize: '12px' }}>
                                        <span className='me-2'>
                                            {huaweiIcon}
                                        </span>
                                        App Gallery ID
                                    </FormLabel>
                                    <Input
                                        // html input attribute
                                        name="appGalleryId"
                                        type="text"
                                        placeholder="C###########"
                                        size='lg'
                                        sx={{ fontSize: '14px' }}
                                    />
                                    <FormHelperText sx={{ fontSize: '12px', fontWeight: 'medium' }}>
                                        The App Gallery ID is a unique identifier assigned to your app within Huawei's AppGallery.
                                        It usually starts with the letter 'C' followed by a series of numbers (e.g., C1234567890).
                                    </FormHelperText>
                                </FormControl>
                            </Sheet>
                        </Stack>
                        <Divider className='my-4' />
                        <div className='text-end'>
                            <Button
                                type='submit'
                                size='lg'
                                sx={{
                                    fontSize: '14px'
                                }}
                            >
                                Save App
                            </Button>
                        </div>
                    </form>
                </div >
            }
        </>

    );
}

export default AddNewAppComponent;